import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CircleAlert } from 'lucide-react';
import IUCNLogo from 'src/assets/images/dashboard/iucn.png';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import usePermissions from 'src/shared/hooks/usePermissions';
import DataTable from 'src/shared/components/data-table/DataTable';
import {
    DataTableColumns,
    DataTableState,
    SortingDirection,
    TableCellRendererParams,
    TableRowData,
    TableRowsSortingFunction,
} from 'src/shared/components/data-table/dataTableTypes';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import Loader from 'src/shared/components/loader/Loader';
import { SecondaryButton } from 'src/shared/components/button/Button';
import Alert from 'src/shared/components/ui/alert';
import DodoImage from 'src/assets/images/general/404.webp';
import Tabs from 'src/shared/components/Tabs/Tabs';
import SearchInput from 'src/shared/components/search-input/SearchInput';
import { useDebouncedListSearch } from 'src/shared/hooks/useDebouncedListSearch';
import IUCNStatusChip from 'src/shared/components/iucn-status-chip/IUCNStatusChip';
import { IUCNStatusCode } from 'src/shared/helpers/iucnStatusHelper';
import SpeciesImage from 'src/app/portfolio/components/species-image/SpeciesImage';
import { Badge } from 'src/shared/components/ui/badge';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import {
    usePortfolioSpeciesSummaryQuery,
    usePortfolioRedListSpeciesQuery,
    usePortfolioInvasiveSpeciesQuery,
} from 'src/app/portfolio/state/api/portfolioSpeciesGraphSlice';
import { PortfolioSpeciesListItem } from 'src/app/portfolio/state/api/portfolioSpeciesQuery';
import SpeciesSummaryStats from './components/SpeciesSummaryStats';
import SpeciesInfoModal from './components/species-info-modal/SpeciesInfoModal';
import ReportingFrameworks from './components/reporting-frameworks/ReportingFrameworks';
import usePortfolioSpeciesRiskAnalytics from './hooks/usePortfolioSpeciesRiskAnalytics';

const ROWS_PER_PAGE = 25;

const RED_LIST_TAB = 'iucn-red-list-species';
const INVASIVE_TAB = 'invasive-species';

const TAB_IDS = [INVASIVE_TAB, RED_LIST_TAB];

/** The tab is the list; its size belongs on it. No count until the summary lands. */
const withCount = (label: string, count?: number) => (count === undefined ? label : `${label} (${count.toLocaleString('en-GB')})`);

const speciesTabs = (invasiveCount?: number, redListCount?: number) => [
    {
        id: INVASIVE_TAB,
        label: (
            <div className='flex items-center gap-2'>
                <CircleAlert className='text-red-100' /> {withCount('Invasive species', invasiveCount)}
            </div>
        ),
    },
    {
        id: RED_LIST_TAB,
        label: (
            <div className='flex items-center gap-2'>
                <img src={IUCNLogo} alt='' className='w-[24px] h-[24x]' /> {withCount('IUCN Red List species', redListCount)}
            </div>
        ),
    },
];

/**
 * Column to sort field. Every value here is one Go accepts — it rejects anything
 * else rather than defaulting, so an unmapped column would raise an error instead
 * of quietly returning an arbitrary order.
 */
const columnIdToSortField: Record<string, string> = {
    speciesName: 'commonName',
    scientificName: 'scientificName',
    iucnStatus: 'iucnCategory',
    siteCoverage: 'siteCount',
};

/** Sorting happens in Go; keep row order so the server's page is what renders. */
const preserveServerSortedRows: TableRowsSortingFunction = rows => [...rows];

/**
 * Fills the empty-state slot rather than using the table's overlay, which can only
 * dim rows that are already there. A page or sort change clears `currentData`, so
 * the table has no rows and would otherwise say the portfolio has no species while
 * the next page is still in flight.
 */
const tableLoading = (
    <div className='flex justify-center py-10' data-testid='species-table-loading'>
        <Loader />
    </div>
);

/**
 * Names the term when one is active. "This portfolio has no invasive species" and
 * "nothing here matches what you typed" are different facts, and reporting the
 * first for the second reads as a data problem rather than a search result.
 */
const emptyStateFor = (isRedListTab: boolean, term?: string) =>
    term
        ? `No ${isRedListTab ? 'IUCN Red List' : 'invasive'} species match “${term}”`
        : `There are no ${isRedListTab ? 'IUCN Red List' : 'Invasive'} species predicted across this portfolio`;

type SpeciesRowMeta = {
    species: PortfolioSpeciesListItem;
    /**
     * The active list's own figure (DD-5796): sites where the species is on the
     * Red List / invasive — not everywhere it is predicted. Falls back to
     * presence when the server predates the per-list counts.
     */
    coverage: { siteCount: number; percentCoverage: number };
    /**
     * On the Red List tab, the sites where this species is ALSO invasive —
     * disclosed on the row so both-tabs membership reads as a fact, not a
     * duplicate. Zero elsewhere.
     */
    alsoInvasiveAt: number;
};

/** What the species info modal needs from the row that opened it. */
type SelectedRowData = {
    commonName: string;
    scientificName: string;
    status: string;
};

const getRowMeta = (rowData: TableRowData): SpeciesRowMeta | null => {
    const firstCell = rowData[0];
    if (!firstCell || typeof firstCell !== 'object' || !('data' in firstCell)) {
        return null;
    }
    const meta = (firstCell as { data?: SpeciesRowMeta }).data;
    return meta?.species ? meta : null;
};

const iucnStatusRenderer = (params: TableCellRendererParams) => {
    if (!getRowMeta(params.rowData)) {
        return null;
    }
    const status = params.cellData as string;
    return status ? <IUCNStatusChip status={status as IUCNStatusCode} /> : null;
};

const siteCoverageRenderer = (params: TableCellRendererParams) => {
    const meta = getRowMeta(params.rowData);
    if (!meta) {
        return null;
    }
    const { percentCoverage, siteCount } = meta.coverage;
    return (
        <span className='flex flex-col'>
            <span className='flex items-cent gap-1'>
                <span className='font-bold'>{percentCoverage}%</span>({siteCount} site{siteCount === 1 ? '' : 's'})
            </span>
            {meta.alsoInvasiveAt > 0 && (
                <span className='text-xs text-grey-80'>
                    also invasive at {meta.alsoInvasiveAt} site{meta.alsoInvasiveAt === 1 ? '' : 's'}
                </span>
            )}
        </span>
    );
};

const speciesNameRenderer = (params: TableCellRendererParams) => {
    const meta = getRowMeta(params.rowData);
    if (!meta) {
        return null;
    }
    const commonName = params.cellData as string;
    return (
        <div className='flex items-center gap-2'>
            <SpeciesImage
                scientificName={meta.species.scientificName}
                alt={commonName}
                width='100px'
                height='56.25px'
                className='rounded-lg'
            />
            <span className='text-primary'>{commonName || meta.species.scientificName}</span>
            {meta.species.isNew && (
                <QuestionButtonHelp
                    icon={<Badge className='text-xs text-black font-normal bg-blue-20 hover:bg-blue-20 border border-blue-60'>New</Badge>}
                    type='api'
                    placement='top'
                    slug='new-species-prediction-badge'
                    zIndex={1300}
                    maxWidth='400px'
                />
            )}
        </div>
    );
};

const redListColumns: DataTableColumns = [
    { columnId: 'metadataRow' },
    {
        columnId: 'speciesName',
        title: 'Species name',
        width: '35%',
        renderer: speciesNameRenderer,
        isSortable: true,
        sortFn: preserveServerSortedRows,
    },
    {
        columnId: 'scientificName',
        title: 'Scientific name',
        width: '25%',
        bodyCellClassName: 'italic',
        isSortable: true,
        sortFn: preserveServerSortedRows,
    },
    {
        columnId: 'iucnStatus',
        title: 'IUCN Red List status',
        width: '20%',
        renderer: iucnStatusRenderer,
        isSortable: true,
        sortFn: preserveServerSortedRows,
    },
    {
        columnId: 'siteCoverage',
        // Named for what it counts (DD-5796): the sites where the species is on
        // this tab's list, never bare presence.
        title: 'Red List sites',
        width: '20%',
        renderer: siteCoverageRenderer,
        isSortable: true,
        sortFn: preserveServerSortedRows,
    },
];

/** Invasive species carry a category but the list is not about it, so it is omitted. */
const invasiveColumns: DataTableColumns = [
    { columnId: 'metadataRow' },
    { ...redListColumns[1], width: '45%' },
    { ...redListColumns[2], width: '30%' },
    { ...redListColumns[4], title: 'Invasive sites', width: '25%' },
];

export const SpeciesRiskIndicators = () => {
    const { currentCustomerId } = useCustomer();
    const { isCurrentUserInternal } = usePermissions();
    // The summary's two species cards each explore into one of these tabs, so the tab
    // travels in the URL. Validated rather than trusted: an unknown value would select
    // no tab at all, leaving the page with an empty table and nothing highlighted.
    const [searchParams] = useSearchParams();
    const urlTab = searchParams.get('tab');
    const [selectedTab, setSelectedTab] = useState<string>(TAB_IDS.includes(urlTab as string) ? (urlTab as string) : TAB_IDS[0]);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState('siteCount');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

    const isRedListTab = selectedTab === RED_LIST_TAB;

    // Deliberately not reset on a tab change: the two lists are different sets, and
    // carrying the term is how "is this species on the other list too?" — the
    // dual-status question — gets answered in one click rather than by retyping.
    const { inputValue: searchInput, activeSearch, onInputChange: onSearchChange } = useDebouncedListSearch();

    // A new term makes the old page number meaningless: page 3 of a set that now
    // has one match asks for a skip past the end and gets an empty page.
    useEffect(() => {
        setCurrentPage(1);
    }, [activeSearch]);

    const summaryQuery = usePortfolioSpeciesSummaryQuery({ customerId: currentCustomerId }, { skip: !currentCustomerId });
    const listArgs = {
        customerId: currentCustomerId,
        pagination: { page: currentPage, pageSize: ROWS_PER_PAGE },
        sortBy: { field: sortField, order: sortOrder },
        // Server-side, because the list is paged: filtering the hundred rows on
        // screen would miss every match on a later page and call it "no results".
        searchText: activeSearch,
    };
    const redListQuery = usePortfolioRedListSpeciesQuery(listArgs, { skip: !currentCustomerId || !isRedListTab });
    const invasiveQuery = usePortfolioInvasiveSpeciesQuery(listArgs, { skip: !currentCustomerId || isRedListTab });

    const summary = summaryQuery.currentData;
    const activeList = isRedListTab ? redListQuery : invasiveQuery;
    // data, not currentData: a page turn or re-sort clears currentData, but data
    // lags one request behind — the previous rows and total stay on screen under
    // the table's inline overlay instead of collapsing to a spinner.
    const list = activeList.data;

    // Surfaced rather than softened: a failed grid read must not render as a
    // portfolio with no species.
    const error = summaryQuery.error || activeList.error;

    const totalItems = list?.pagination.total ?? 0;
    // The active list's size from the summary, which no search ever narrows —
    // the denominator the match count is "of".
    const listSize = isRedListTab ? summary?.redListSpeciesCount : summary?.invasiveSpeciesCount;

    const tableData = useMemo(() => {
        if (!list) {
            return [];
        }
        const siteCount = summary?.siteCount ?? 0;
        return list.species.map(species => {
            // The active list's own figure (DD-5796); presence when the server
            // predates the per-list counts (null — unknown, never zero).
            const coverageSites = (isRedListTab ? species.redListSiteCount : species.invasiveSiteCount) ?? species.siteCount;
            const meta: SpeciesRowMeta = {
                species,
                coverage: {
                    siteCount: coverageSites,
                    percentCoverage: siteCount > 0 ? Math.floor((coverageSites / siteCount) * 100) : 0,
                },
                alsoInvasiveAt: isRedListTab ? species.invasiveSiteCount ?? 0 : 0,
            };
            if (isRedListTab) {
                return [{ data: meta }, species.commonName, species.scientificName, species.iucnRedListCategory, species.siteCount];
            }
            return [{ data: meta }, species.commonName, species.scientificName, species.siteCount];
        });
    }, [list, summary, isRedListTab]);

    const trackAnalytics = usePortfolioSpeciesRiskAnalytics();
    const [selectedRowData, setSelectedRowData] = useState<SelectedRowData | null>(null);
    const [tableState, setTableState] = useState<Partial<DataTableState>>({
        fixedHeader: true,
        hiddenColumnIds: new Set(['metadataRow']),
        rowsPerPage: ROWS_PER_PAGE,
        sortedColumn: { columnId: 'siteCoverage', sortedDirection: SortingDirection.DESC },
    });

    useEffect(() => {
        setTableState(prev => ({
            ...prev,
            data: tableData,
            currentPageNumber: currentPage,
            totalPages: Math.ceil(totalItems / ROWS_PER_PAGE),
        }));
    }, [tableData, currentPage, totalItems]);

    const handleStateChange = (newState: Partial<DataTableState>) => {
        setTableState(newState);
        if (newState.currentPageNumber && newState.currentPageNumber !== currentPage) {
            setCurrentPage(newState.currentPageNumber);
        }
        if (newState.sortedColumn) {
            const field = columnIdToSortField[newState.sortedColumn.columnId] || 'siteCount';
            const order = newState.sortedColumn.sortedDirection === SortingDirection.ASC ? ('ASC' as const) : ('DESC' as const);
            if (field !== sortField || order !== sortOrder) {
                setSortField(field);
                setSortOrder(order);
                setCurrentPage(1);
            }
        }
    };

    if (summaryQuery.isFetching && !summary) {
        return <StandardAppLayout mainContent={<Loader />} title='Portfolio species risks' subTitle='' />;
    }

    return (
        <>
            <StandardAppLayout
                title='Portfolio species risks'
                subTitle='Predicted species of interest across your portfolio'
                // Tighter than the layout's default 20px below the subtitle
                // (!important: the module's padding otherwise wins the cascade).
                classNames={{ topContent: '!pb-2' }}
                mainContent={
                    error ? (
                        // A failed read surfaces as an honest gap, not a red alert — and
                        // never as a portfolio with zero species.
                        <div className='flex flex-col items-center gap-6 pt-16 text-center' data-testid='species-error'>
                            <div className='flex flex-col items-center'>
                                <img src={DodoImage} alt='' className='h-40 w-auto' />
                                <div className='h-3 w-24 -mt-2 rounded-[50%] bg-grey-40/50 blur-[3px]' />
                            </div>
                            <div className='flex flex-col items-center gap-2 max-w-lg'>
                                <h3 className='text-xl font-semibold text-primary'>Species predictions are not available right now</h3>
                                <p className='text-base text-grey-70'>
                                    This is a temporary problem. Try again later. If the problem continues, contact your account manager.
                                </p>
                            </div>
                            <SecondaryButton
                                onClick={() => {
                                    summaryQuery.refetch();
                                    activeList.refetch();
                                }}
                            >
                                Retry
                            </SecondaryButton>
                        </div>
                    ) : (
                        <div className='flex flex-col gap-4'>
                            <div className='w-full border-b border-grey-20'>
                                <Tabs
                                    tabs={speciesTabs(summary?.invasiveSpeciesCount, summary?.redListSpeciesCount)}
                                    selectedTab={selectedTab}
                                    onTabChange={tab => {
                                        // The event names the page it replaced sent (same tab ids),
                                        // so dashboards spanning the cutover keep reading.
                                        trackAnalytics.trackTabClick({ tab });
                                        setSelectedTab(tab);
                                        setCurrentPage(1);
                                    }}
                                    classNames={{
                                        tabsContainer: 'flex items-center',
                                        // Both states carry border-b-2 so selecting never changes the tab's
                                        // height; !important because default's transparent border-color
                                        // otherwise wins the stylesheet-order fight and hides the line.
                                        default: 'px-3 py-4 text-[17px] text-black border-b-2 border-[transparent]',
                                        selected: 'text-primary border-b-2 !border-primary',
                                    }}
                                />
                            </div>
                            {/* The set's figures in words: union total, each list's count, the overlap. */}
                            <div className='flex items-center gap-6 px-4 py-3 bg-grey-20 rounded-lg text-sm' data-testid='species-summary'>
                                <div className='flex-1 min-w-0'>
                                    <SpeciesSummaryStats
                                        redListCount={summary?.redListSpeciesCount ?? 0}
                                        invasiveCount={summary?.invasiveSpeciesCount ?? 0}
                                        totalCount={summary?.totalSpeciesCount ?? 0}
                                        siteCount={summary?.siteCount ?? 0}
                                        snapshot={summary?.snapshot}
                                    />
                                </div>
                                <div className='self-start'>
                                    <ReportingFrameworks />
                                </div>
                            </div>

                            {/* An ops signal, not a customer fact: missing boundaries are
                            ours to fix, so the gap is reported to internal users only. */}
                            {isCurrentUserInternal && !!summary?.sitesWithoutGeometry.length && (
                                <Alert severity='warning' data-testid='species-sites-without-geometry'>
                                    {summary.sitesWithoutGeometry.length} site
                                    {summary.sitesWithoutGeometry.length === 1
                                        ? ' has no boundary and is not included: '
                                        : 's have no boundary and are not included: '}
                                    {summary.sitesWithoutGeometry.map(site => site.siteName).join(', ')}
                                </Alert>
                            )}

                            {/*
                             * The count beside the box, never on the tabs: the tab
                             * counts and the stats strip describe the portfolio, and
                             * a filter must not appear to shrink it.
                             */}
                            <div className='flex items-center gap-3'>
                                <SearchInput
                                    aria-label='Search species'
                                    placeholder='Search by species or common name'
                                    value={searchInput}
                                    onChange={onSearchChange}
                                    width='320px'
                                />
                                {activeSearch && (
                                    <span className='text-sm text-grey-80' data-testid='species-match-count'>
                                        {totalItems.toLocaleString('en-GB')} of {(listSize ?? 0).toLocaleString('en-GB')} matching “
                                        {activeSearch}”
                                    </span>
                                )}
                            </div>

                            <DataTable
                                key={selectedTab}
                                paginationPosition='top'
                                topPaginationAside={
                                    <div className='text-sm text-grey-80'>
                                        {isRedListTab
                                            ? 'CR, EN, VU and NT species, counted at sites where they are not invasive.'
                                            : "Species classified invasive in the country of at least one site's location."}
                                    </div>
                                }
                                // Fill the viewport below the page chrome (~240px of header,
                                // tabs, stats strip and pager, plus breathing room) instead of
                                // a fixed fraction — a 65vh cap left a dead band under the
                                // table on tall screens.
                                style={{ maxHeight: 'calc(100vh - 260px)' }}
                                state={tableState}
                                columns={isRedListTab ? redListColumns : invasiveColumns}
                                onStateChange={handleStateChange}
                                onRowSelect={(rowData: TableRowData) => {
                                    const meta = getRowMeta(rowData);
                                    if (!meta) {
                                        return;
                                    }
                                    trackAnalytics.trackSpeciesRiskModalOpen({ tab: selectedTab });
                                    setSelectedRowData({
                                        commonName: rowData[1] as string,
                                        scientificName: rowData[2] as string,
                                        status: meta.species.iucnRedListCategory,
                                    });
                                }}
                                totalRowsForPagination={totalItems}
                                isServerSideSort
                                isLoadingOverlay={activeList.isFetching && !!list}
                                emptyStateMessage={activeList.isFetching ? tableLoading : emptyStateFor(isRedListTab, activeSearch)}
                                // 5px less air above the pager than the page's 16px gap allows.
                                className='overflow-visible mt-[-5px]'
                                tableClassName='h-full'
                                allowSortClear={false}
                            />
                        </div>
                    )
                }
            />
            {selectedRowData && (
                <SpeciesInfoModal
                    commonName={selectedRowData.commonName}
                    scientificName={selectedRowData.scientificName}
                    initialTab={isRedListTab ? 'redList' : 'invasive'}
                    iucnStatus={selectedRowData.status}
                    totalSiteCount={summary?.siteCount}
                    onClose={() => setSelectedRowData(null)}
                />
            )}
        </>
    );
};

export default SpeciesRiskIndicators;
